import { useMemo } from "react";
import { useQuery } from "convex/react";
import { api } from "@bindery/convex-retells/_generated/api";
import {
  CONTEXT_LICENSES,
  type ContextLicense,
  type ContextLink,
} from "@bindery/shared/corpus/document";
import { Skeleton } from "@bindery/ui/components/ui/skeleton";
import { ErrorBoundary } from "@bindery/ui/components/error-boundary";
import { useLogger } from "@/logging";
import {
  parseExcerptMarkdown,
  type ExcerptList,
  type ExcerptNode,
} from "./excerpt-markdown";

type ExcerptLink = Pick<ContextLink, "excerptId" | "attribution">;

/**
 * The excerpt behind the read-more gate. Mounted only once the gate opens, so
 * its query is the lazy fetch: a reader who never asks downloads nothing.
 */
export function ContextExcerpt({ link }: { link: ExcerptLink }) {
  const log = useLogger();
  const { excerptId } = link;
  return (
    <ErrorBoundary
      onError={(error) => {
        log.error("context_link.excerpt_error", error, { excerptId });
      }}
      fallback={(_error, reset) => (
        <p className="text-muted-foreground font-serif text-sm">
          The excerpt couldn’t be loaded.{" "}
          <button
            type="button"
            onClick={reset}
            className="text-lookup underline underline-offset-2"
          >
            Try again
          </button>
        </p>
      )}
    >
      <ExcerptQuery link={link} />
    </ErrorBoundary>
  );
}

function ExcerptQuery({ link }: { link: ExcerptLink }) {
  const excerpt = useQuery(api.contextLinks.getExcerpt, {
    excerptId: link.excerptId,
  });
  if (excerpt === undefined) {
    return (
      <div aria-busy="true" aria-label="Loading excerpt" className="space-y-2">
        <Skeleton className="h-3.5 w-full" />
        <Skeleton className="h-3.5 w-11/12" />
        <Skeleton className="h-3.5 w-4/5" />
      </div>
    );
  }
  if (excerpt === null) {
    return (
      <p className="text-muted-foreground font-serif text-sm">
        This excerpt isn’t available right now.
      </p>
    );
  }
  return (
    <>
      <ExcerptBody body={excerpt.body} />
      {/* One excerpt row serves every revision pinning its text, so the
          link's own pin is the revision this reader was shown. */}
      <ExcerptAttribution
        title={link.attribution.title}
        revisionUrl={link.attribution.revisionUrl}
        license={link.attribution.license}
      />
    </>
  );
}

function ExcerptBody({ body }: { body: string }) {
  const nodes = useMemo(() => parseExcerptMarkdown(body), [body]);
  return (
    <div className="text-foreground/90 space-y-2.5 font-serif text-base leading-relaxed">
      {nodes.map((node, i) => (
        <ExcerptBlock key={i} node={node} />
      ))}
    </div>
  );
}

function ExcerptBlock({ node }: { node: ExcerptNode }) {
  switch (node.kind) {
    case "heading":
      return (
        <h3 className="text-foreground pt-1 font-serif text-base font-bold tracking-tight">
          {node.text}
        </h3>
      );
    case "paragraph":
      return <p>{node.text}</p>;
    case "quote":
      return (
        <blockquote className="border-rule text-foreground/75 border-l-2 pl-3 italic">
          {node.text}
        </blockquote>
      );
    case "list":
      return <ExcerptListView list={node.list} />;
  }
}

function ExcerptListView({ list }: { list: ExcerptList }) {
  const items = list.items.map((item, i) => (
    <li key={i}>
      {item.text}
      {item.sublist && <ExcerptListView list={item.sublist} />}
    </li>
  ));
  return list.ordered ? (
    <ol className="list-decimal space-y-1 pl-5">{items}</ol>
  ) : (
    <ul className="list-disc space-y-1 pl-5">{items}</ul>
  );
}

function ExcerptAttribution({
  title,
  revisionUrl,
  license,
}: {
  title: string;
  revisionUrl: string;
  license: ContextLicense;
}) {
  const terms = CONTEXT_LICENSES[license];
  const linkClass =
    "text-foreground/80 hover:text-lookup underline decoration-rule underline-offset-2";
  return (
    <p className="border-rule text-muted-foreground mt-4 border-t border-dotted pt-3 font-serif text-xs leading-relaxed">
      An excerpt from{" "}
      <a
        href={revisionUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClass}
      >
        {title}
      </a>
      , Simple English Wikipedia, available under{" "}
      <a
        href={terms.url}
        target="_blank"
        rel="noopener noreferrer license"
        className={linkClass}
      >
        {terms.name}
      </a>
      .
    </p>
  );
}
